"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";

const faqs = [
  {
    q: "How quickly will I get a response?",
    a: "We respond to all inquiries within 24 hours during business days (Monday-Friday). Urgent issues related to lost pets or emergency access are prioritized and typically answered within 2-4 hours.",
  },
  {
    q: "Can I schedule a demo or product walkthrough?",
    a: "Yes! Business partners can request a personalized demo by selecting \'Business Partnership\' in the contact form. We\'ll schedule a 30-minute video call to walk you through the platform and answer your questions.",
  },
  {
    q: "Where can I find help documentation?",
    a: "Our Help Center is coming soon. In the meantime, check out the 'How It Works' page for detailed guides, or reach out directly with specific questions.",
  },
  {
    q: "How do I report a bug or technical issue?",
    a: "Select 'Bug Report' or 'Technical Support' in the contact form and describe the issue in detail. Include screenshots if possible. Our engineering team reviews all bug reports within 48 hours.",
  },
  {
    q: "Can I suggest a new feature?",
    a: "Absolutely! We love hearing from our community. Choose \'Feature Request\' in the form and tell us what you\'d like to see. We review all suggestions and prioritize based on user demand.",
  },
];

export default function ContactFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target
              .querySelectorAll(".scroll-fade-up")
              .forEach((el, i) => {
                setTimeout(() => el.classList.add("is-visible"), i * 80);
              });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-14 scroll-fade-up">
          <span className="teal-badge mb-4">FAQ</span>
          <h2 className="text-4xl font-extrabold text-[#1A2B2B] tracking-tight mt-4 mb-4">
            Common questions about support
          </h2>
          <p className="text-[#4A6363] text-lg">
            Quick answers to help you get the support you need.
          </p>
        </div>

        <div className="flex flex-col gap-0 mb-10">
          {faqs?.map((faq, idx) => (
            <div key={`contact-faq-${idx}`} className="scroll-fade-up faq-item">
              <button
                className="w-full flex items-center justify-between py-5 text-left gap-4 hover:text-teal-600 transition-colors duration-200"
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                aria-expanded={openIndex === idx}
              >
                <span className="text-base font-semibold text-[#1A2B2B] font-heading pr-2">
                  {faq?.q}
                </span>
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                    openIndex === idx
                      ? "bg-teal-500 rotate-45" :"bg-white border border-[#E2EDED]"
                  }`}
                >
                  <Icon
                    name="PlusIcon"
                    size={14}
                    className={openIndex === idx ? "text-white" : "text-[#4A6363]"}
                  />
                </div>
              </button>
              <div
                className="faq-answer"
                style={{ maxHeight: openIndex === idx ? "300px" : "0" }}
              >
                <p className="pb-5 text-[#4A6363] leading-relaxed text-sm">{faq?.a}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="scroll-fade-up text-center p-8 bg-hero-gradient rounded-3xl border border-[#E2EDED]">
          <h3 className="text-2xl font-bold text-[#1A2B2B] font-heading mb-3">
            Still have questions?
          </h3>
          <p className="text-[#4A6363] mb-6">
            Can't find what you're looking for? Check out our detailed guides or send us a message.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link href="/how-it-works" className="btn-secondary">
              <Icon name="BookOpenIcon" size={18} />
              How It Works
            </Link>
            <Link href="#" className="btn-primary">
              <Icon name="ChatBubbleLeftRightIcon" size={18} />
              Start a Conversation
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}